import mustache from "mustache";
import {
  IGetListYearGroupService,
  YearGroup,
} from "../../../abstractions/services/actions/IGetListYearGroupService";
import { BaseResponse } from "../../../base/response/BaseResponse";
import { IConnectRender } from "../../definitions/IConnectRender";
import template from "./yearGroupSelectorTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let YEAR_GROUP_SELECTOR_ID = 0;

/** Base de selector compacto año → item (Render/Connect): dos `<select>`, uno
 *  de año y uno de los items de ese año. Carga los `YearGroup<T>` del servicio,
 *  puebla el año más reciente y sus items por defecto, y notifica la selección
 *  (inicial y en cada cambio) vía `onSelectionChange`. Pensado para embeberse
 *  repetido (p. ej. paneles de comparación); para un sidebar completo con
 *  acordeón usar `BaseSecondSidebarRender`. */
export abstract class BaseYearGroupSelectorRender<T extends BaseResponse>
  implements IConnectRender
{
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _selectorId: number;

  constructor() {
    YEAR_GROUP_SELECTOR_ID += 1;
    this._selectorId = YEAR_GROUP_SELECTOR_ID;
  }

  /** Etiqueta opcional sobre el selector (p. ej. "Panel A"). */
  protected get label(): string | undefined {
    return undefined;
  }

  /** Servicio que provee los items agrupados por año. */
  protected abstract getYearGroupListService(): IGetListYearGroupService<T>;

  /** Texto a mostrar de un item en el `<select>`. */
  protected abstract getItemLabel(item: T): string;

  /** Notifica el item seleccionado (al cargar y en cada cambio de año/item). */
  protected abstract onSelectionChange(item: T): void;

  render(): string {
    return mustache.render(template, {
      id: this._selectorId,
      label: this.label,
    });
  }

  /** Captura los selects y carga los year groups. */
  connect(): void {
    this._yearSelect = document.getElementById(
      `ygs-year-${this._selectorId}`,
    ) as HTMLSelectElement | null;
    this._itemSelect = document.getElementById(
      `ygs-item-${this._selectorId}`,
    ) as HTMLSelectElement | null;

    this._yearSelect?.addEventListener("change", this._onYearChange);
    this._itemSelect?.addEventListener("change", this._onItemChange);

    void this._loadAsync();
  }

  /** Libera los listeners. */
  dispose(): void {
    this._disposed = true;
    this._yearSelect?.removeEventListener("change", this._onYearChange);
    this._itemSelect?.removeEventListener("change", this._onItemChange);
  }

  private _groups: YearGroup<T>[] = [];
  /** Items del año actualmente aplicado (para resolver la selección por índice). */
  private _currentItems: T[] = [];
  private _yearSelect: HTMLSelectElement | null = null;
  private _itemSelect: HTMLSelectElement | null = null;
  private _disposed = false;

  /** Carga los year groups y aplica el más reciente. */
  private async _loadAsync(): Promise<void> {
    const groups = await this.getYearGroupListService().getListAsync();
    if (this._disposed) return;
    this._groups = groups;
    if (groups.length === 0 || !this._yearSelect) return;

    this._yearSelect.innerHTML = groups
      .map((g) => `<option value="${g.year}">${g.year}</option>`)
      .join("");
    this._applyYear(groups[0].year);
  }

  /** Puebla el select de items del año y selecciona el primero. */
  private _applyYear(year: number): void {
    const items = this._groups.find((g) => g.year === year)?.items ?? [];
    this._currentItems = items;
    if (!this._itemSelect) return;

    this._itemSelect.innerHTML = items
      .map((item, i) => `<option value="${i}">${this.getItemLabel(item)}</option>`)
      .join("");
    if (items.length > 0) this.onSelectionChange(items[0]);
  }

  private _onYearChange = (): void => {
    if (!this._yearSelect) return;
    this._applyYear(Number(this._yearSelect.value));
  };

  private _onItemChange = (): void => {
    if (!this._itemSelect) return;
    const item = this._currentItems[Number(this._itemSelect.value)];
    if (item) this.onSelectionChange(item);
  };
}
